// src/pages/PostJobPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { jobRoles, jobLocations } from '../utils/constants';
import './PostJobPage.css';

const PostJobPage = () => {
    const { firebaseUser, currentUser } = useAuth();
    const navigate = useNavigate();
    const [jobType, setJobType] = useState('private');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const isAdmin = currentUser?.role === 'admin'; 

    const [formData, setFormData] = useState({
        jobRole: '', title: '', companyName: '', qualification: '', vacancies: '',
        experience: '', locationState: '', locationDistrict: '', description: '',
        age: '', salary: '', contactDetails: '', applyLink: '', lastDate: '',
        eligibility: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- NEW: RAZORPAY PAYMENT FLOW ---
    const handlePaidPosting = async () => {
        setLoading(true);
        try {
            const idToken = await firebaseUser.getIdToken(true);

            // 1. Create Order (Reusing your 100/- order logic)
            const orderRes = await axios.post(
                `${API_BASE_URL}/repost-job/create-order`, 
                {}, 
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            const order = orderRes.data;

            // 2. Open Razorpay Checkout
            const options = {
                key: "rzp_test_RR0L9EVjK2DOXO", // Use your Test/Live Key ID
                amount: order.amount,
                currency: "INR",
                name: "AiNews Jobs",
                description: "Private Job Posting Fee",
                order_id: order.id,
                handler: async (response) => {
                    // 3. Verify Payment and SAVE the job
                    try {
                        await axios.post(
                            `${API_BASE_URL}/verify-new-paid-job`,
                            {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                jobData: { ...formData, jobType: 'private' }
                            },
                            { headers: { Authorization: `Bearer ${idToken}` } }
                        );
                        setSuccess("Payment successful! Job posted.");
                        setTimeout(() => navigate('/jobs'), 2000);
                    } catch (verifyErr) {
                        setError("Payment successful, but failed to save job. Please contact support.");
                        setLoading(false);
                    }
                },
                prefill: {
                    email: firebaseUser.email,
                },
                theme: { color: "#488AFF" },
                modal: { ondismiss: () => setLoading(false) }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            setError("Could not initiate payment. Please try again.");
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const idToken = await firebaseUser.getIdToken(true);
            const postData = { ...formData, jobType };

            // Attempt free post
            const response = await axios.post(`${API_BASE_URL}/jobs`, postData, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            
            setSuccess(response.data.message);
            setTimeout(() => navigate('/jobs'), 2000);

        } catch (err) {
            const backendError = err.response?.data;
            
            // --- CRITICAL FIX: Handle the 403 Payment Required ---
            if (backendError?.errorCode === "PAYMENT_REQUIRED") {
                setLoading(false);
                const proceed = window.confirm(
                    "You have already used your free job post.\n\nWould you like to pay ₹100 to post this job?"
                );
                if (proceed) {
                    handlePaidPosting();
                }
            } else {
                setError(backendError?.message || "Failed to post job. Please check all fields.");
                setLoading(false);
            }
            console.error("Submission error:", err);
        }
    };

    const isStateWithDistricts = formData.locationState && jobLocations[formData.locationState]?.length > 0;

    // A helper for required fields
    const Req = () => <span className="required">*</span>;

    return (
        <div className="post-job-page">
            <h1>Post a Job</h1>
            
            <div className="job-type-selector">
                <label><input type="radio" name="jobType" value="private" checked={jobType === 'private'} onChange={(e) => setJobType(e.target.value)} />Job</label>
                <label><input type="radio" name="jobType" value="government" checked={jobType === 'government'} onChange={(e) => setJobType(e.target.value)} /> Government Job Notifications</label>
            </div>

            {error && <p className="error-message">{error}</p>}
            {success && <p className="success-message">{success}</p>}

            <form onSubmit={handleSubmit} className="post-job-form">
                {jobType === 'private' && (
                    <>
                        <div className="form-group"><label>Job Role <Req /></label><select name="jobRole" value={formData.jobRole} onChange={handleChange} required><option value="">Select Role...</option>{jobRoles.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                        <div className="form-group"><label>Title <Req /></label><input type="text" name="title" value={formData.title} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Company Name <Req /></label><input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required /></div>
                        <div className="form-row">
                            <div className="form-group"><label>Qualification <Req /></label><input type="text" name="qualification" value={formData.qualification} onChange={handleChange} required /></div>
                            <div className="form-group"><label>Vacancies <Req /></label><input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} required /></div>
                        </div>
                        <div className="form-group"><label>Experience <Req /></label><input type="text" name="experience" value={formData.experience} onChange={handleChange} required /></div>
                        <div className="form-row">
                            <div className="form-group"><label>Job Location <Req /></label><select name="locationState" value={formData.locationState} onChange={handleChange} required><option value="">Select Location...</option>{Object.keys(jobLocations).map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                            {isStateWithDistricts && <div className="form-group"><label>District (Optional)</label><select name="locationDistrict" value={formData.locationDistrict} onChange={handleChange}><option value="">All Districts</option>{jobLocations[formData.locationState].map(d => <option key={d} value={d}>{d}</option>)}</select></div>}
                        </div>
                        <div className="form-group"><label>Description (Optional)</label><textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea></div>
                        <div className="form-row">
                            <div className="form-group"><label>Age (Optional)</label><input type="text" name="age" value={formData.age} onChange={handleChange} /></div>
                            <div className="form-group"><label>Salary <Req /></label><input type="text" name="salary" value={formData.salary} onChange={handleChange} required /></div>
                        </div>
                        <div className="form-group"><label>Contact Details <Req /></label><input type="text" name="contactDetails" value={formData.contactDetails} onChange={handleChange} required /></div>
                        <div className="form-row">
                            <div className="form-group"><label>Apply Link (Optional)</label><input type="url" name="applyLink" value={formData.applyLink} onChange={handleChange} /></div>
                            <div className="form-group"><label>Last Date (Optional)</label><input type="date" name="lastDate" value={formData.lastDate} onChange={handleChange} /></div>
                        </div>
                    </>
                )}
                {jobType === 'government' && (
                    <>
                        <div className="form-group"><label>Title <Req /></label><input type="text" name="title" value={formData.title} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Eligibility <Req /></label><input type="text" name="eligibility" value={formData.eligibility} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Vacancies <Req /></label><input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Salary/Stipend <Req /></label><input type="text" name="salary" value={formData.salary} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Last Date <Req /></label><input type="date" name="lastDate" value={formData.lastDate} onChange={handleChange} required /></div>
                        <div className="form-group"><label>Apply Link <Req /></label><input type="url" name="applyLink" value={formData.applyLink} onChange={handleChange} required /></div>
                        <div className="form-group"><label>More Info Link (Optional)</label><input type="url" name="moreInfoLink" value={formData.moreInfoLink} onChange={handleChange} /></div>
                    </>
                )}
                <div className="submit-btn-container">
                    <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'Submitting...' : 'Submit Job Post'}</button>
                    {/* Only show warning to non-admins */}
                    {!isAdmin && jobType === 'private' && (
                        <p className="payment-warning-text">
                            * Private job posts are valid for 30 days. Payments are non-refundable.
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
};

export default PostJobPage;